import { useMemo, useState } from 'react';

import { useIsBelowMd } from '~/ui-rocco/hooks/use-is-below-md';
import { cn } from '@rocco/utils/cn';
import { detailCta, DetailTileCard } from '~/ui-rocco/pages/detail-shared';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared/LoadMoreButton';
import { useInfiniteScrollSentinel } from '~/ui-rocco/pages/category-page/hooks/useInfiniteScrollSentinel';
import {
    PRODUCT_TILE_GRID,
    PRODUCT_TILE_MEDIA,
    PRODUCT_TILE_SUBTITLE,
    PRODUCT_TILE_TITLE,
} from './product-tile-grid';
import type { SimilarProducts } from './use-similar-products';

/**
 * Inner content of the Similar Products block (Figma 1841:9733/10119/10162) —
 * the two-tone "More in {category} category" heading, the category breadcrumb,
 * the 2-col (mobile) / 4-col (desktop) tile grid and the "Load More" pill. No
 * eyebrow / hairline / leading spacer: the standalone section and the accordion
 * row each supply their own surrounding chrome.
 */
export const SimilarProductsGrid = ({
    tiles,
    hasMore,
    isLoading,
    loadMore,
    hideLoadMore = false,
    tileTextClassName,
}: SimilarProducts & {
    /** Suppress the inline pill when the Magic Island owns "Load More". */
    hideLoadMore?: boolean;
    /** Extra classes on each tile's text (the accordion row runs it smaller). */
    tileTextClassName?: string;
}) => {
    // While more products are coming, the grid draws whole rows only: a page
    // size that the column count does not divide otherwise leaves the last row
    // as one or two tiles beside empty cells, which reads as missing content
    // rather than as more to load. The held-back tiles arrive with the next
    // page. At the end of the results the short row is the honest shape, so it
    // stays. Columns mirror PRODUCT_TILE_GRID — 2 below md, 5 from md.
    const columns = useIsBelowMd() ? 2 : 5;
    const rowAligned = useMemo(() => {
        if (!hasMore || tiles.length < columns) return tiles;
        return tiles.slice(0, Math.floor(tiles.length / columns) * columns);
    }, [tiles, hasMore, columns]);

    // Same hybrid as the category grid: Load More is a real, crawlable control
    // for the first step, and arms infinite scroll for everything after it, so
    // the reader opts in once rather than the page growing unasked.
    const [infiniteEnabled, setInfiniteEnabled] = useState(false);
    // The shared sentinel keeps one observer for the element's lifetime. The
    // observer must not be rebuilt per load: a fresh one reports an
    // already-intersecting target immediately, so tearing it down and
    // recreating it each cycle fetched again with no reader input at all.
    //
    // The margin has to be something a single page clears, or the sentinel
    // never leaves the zone and paging runs to the end of the category on its
    // own. A page here is 4 tiles over two mobile columns — two rows — where
    // the grid's own 2000px default would never be cleared.
    const setSentinelRef = useInfiniteScrollSentinel({
        enabled: infiniteEnabled && hasMore && !isLoading,
        onTrigger: () => loadMore?.(),
        rootMarginPx: 600,
    });

    return (
        <>
            <div className={PRODUCT_TILE_GRID}>
                {rowAligned.map((tile) => (
                    <DetailTileCard
                        key={tile.id}
                        tile={tile}
                        // As More products does: the `•••` under the picture.
                        mobileActionsInCaption
                        mediaClassName={PRODUCT_TILE_MEDIA}
                        textClassName={cn(
                            PRODUCT_TILE_TITLE,
                            tileTextClassName,
                        )}
                        subtitleClassName={PRODUCT_TILE_SUBTITLE}
                    />
                ))}
            </div>

            {hasMore && !hideLoadMore && !infiniteEnabled ? (
                <LoadMoreButton
                    className="mt-[6rem] max-md:mt-20"
                    isLoading={isLoading}
                    onClick={() => {
                        setInfiniteEnabled(true);
                        loadMore?.();
                    }}
                />
            ) : null}
            {hasMore && infiniteEnabled ? (
                <div ref={setSentinelRef} aria-hidden="true" className="h-px" />
            ) : null}
        </>
    );
};
